import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { move } from '@dnd-kit/helpers';
import { toast } from 'react-toastify';
import { updateEpmTest } from '../../../utils/APIs';
import { COLUMN_ORDER } from './constants';
import { iceScore } from '../epmHelpers';

/**
 * Custom hook managing drag-and-drop state, server data normalization,
 * optimistic updates, and database persistence for the EPM testing board.
 */
export function useBoardDnD({ tests, setTests, userData }) {
    // Group tests by status and normalize with an `id` string for @dnd-kit
    const serverItems = useMemo(() => {
        const grouped = Object.fromEntries(COLUMN_ORDER.map((col) => [col, []]));
        (tests || []).forEach((test) => {
            if (grouped[test.status]) {
                grouped[test.status].push({
                    ...test,
                    id: String(test._id || test.id),
                });
            }
        });
        // Prioritised is the backlog, so it is ranked by ICE (storyboard 10)
        grouped.PRIORITISED.sort((a, b) => (iceScore(b) ?? -1) - (iceScore(a) ?? -1));
        return grouped;
    }, [tests]);

    // Local drag-and-drop state
    const [items, setItems] = useState(serverItems);
    const previousItemsRef = useRef(items);
    const itemsRef = useRef(items);

    // Keep itemsRef synchronized for event callbacks
    useEffect(() => {
        itemsRef.current = items;
    }, [items]);

    // Sync local state when server data updates
    useEffect(() => {
        setItems(serverItems);
        itemsRef.current = serverItems;
    }, [serverItems]);

    // ── Drag & Drop Callbacks ─────────────────────────────────────────────────

    const handleDragStart = useCallback(() => {
        // Snapshot the current layout before drag begins
        previousItemsRef.current = itemsRef.current;
    }, []);

    const handleDragOver = useCallback((event) => {
        const { source } = event.operation;
        if (source?.type === 'column') return;
        setItems((prev) => {
            const next = move(prev, event);
            itemsRef.current = next;
            return next;
        });
    }, []);

    const handleDragEnd = useCallback(
        async (event) => {
            const { source, canceled } = event.operation;

            // Revert if drag was canceled by user
            if (canceled) {
                if (source.type === 'item') {
                    setItems(previousItemsRef.current);
                }
                return;
            }

            // Columns are not draggable, so we don't need to handle that case
            if (source.type === 'column') {
                return;
            }

            // Handle item moving across columns
            if (source.type === 'item') {
                const prevLayout = previousItemsRef.current;
                const currentLayout = itemsRef.current;

                // Find original column from before drag started
                let initialColumn = null;
                for (const [col, colItems] of Object.entries(prevLayout)) {
                    if (colItems.some((it) => it.id === source.id)) {
                        initialColumn = col;
                        break;
                    }
                }

                // Find destination column where the card landed
                let targetColumn = null;
                for (const [col, colItems] of Object.entries(currentLayout)) {
                    if (colItems.some((it) => it.id === source.id)) {
                        targetColumn = col;
                        break;
                    }
                }

                // Fallback: check target directly if card wasn't found in currentLayout
                if (!targetColumn && event.operation.target) {
                    const targetId = event.operation.target.id;
                    if (COLUMN_ORDER.includes(targetId)) {
                        targetColumn = targetId;
                    }
                }

                // If moved to a different column/status, persist update in DB
                if (targetColumn && initialColumn && targetColumn !== initialColumn) {
                    const token = userData?.token;
                    const orgToken = userData?.selectedOrganization?.org_token;

                    try {
                        await updateEpmTest(
                            token,
                            orgToken,
                            source.id,
                            { status: targetColumn }
                        );

                        // Optimistically update test status in parent context
                        if (setTests) {
                            setTests((prevTests) =>
                                prevTests.map((t) =>
                                    String(t._id || t.id) === String(source.id)
                                        ? { ...t, status: targetColumn }
                                        : t
                                )
                            );
                        }
                    } catch (err) {
                        console.error('Failed to update test status in DB:', err);
                        toast.error(err?.message || 'Failed to update test status in database');
                        // Revert visual state on failure
                        setItems(previousItemsRef.current);
                    }
                }
            }
        },
        [userData?.token, userData?.selectedOrganization?.org_token, setTests]
    );

    return {
        items,
        columnOrder: COLUMN_ORDER,
        handleDragStart,
        handleDragOver,
        handleDragEnd,
    };
}
